import React, { useCallback, useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { GeodataSetupContext } from '../../../../state/geodata-setup-state';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Select } from '../../../select/select';
import { Stack } from '../../../stack/stack';
import {
  GeodataConfiguration,
  GeodataConfigurationData,
} from '../../geodata-configuration/geodata-configuration';

export const GeodataSettingsPage = () => {
  const { t } = useTranslation();
  const context = useContext(GeodataSetupContext);

  const skipGcpStepTooltip = useMemo(
    () => (
      <Stack spacing={0.5}>
        <span>
          <strong>{t('geodataSetup.skipGcpStep', { ns: 'cloudProcessing' })}</strong>
        </span>
        <span>{t('geodataSetup.skipGcpStepTooltip', { ns: 'cloudProcessing' })}</span>
      </Stack>
    ),
    [t],
  );

  const gcpAdjustmentTitle = t('setupDialog.addOns.gcpAdjustment.cardTitle', {
    ns: 'cloudProcessing',
  });

  const useInteractiveSetupTooltip = useMemo(
    () => (
      <Stack spacing={0.5}>
        <span>
          <strong>{t('geodataSetup.useInteractiveSetup', { ns: 'cloudProcessing' })}</strong>
        </span>
        <span>
          <Trans
            components={{ br: <br /> }}
            i18nKey="geodataSetup.useInteractiveSetupTooltip"
            ns="cloudProcessing"
            values={{ name: gcpAdjustmentTitle }}
          />
        </span>
      </Stack>
    ),
    [t, gcpAdjustmentTitle],
  );

  const yesNoOptions = useMemo(
    () => [
      { id: 'yes', name: t('yes', { ns: 'common' }) },
      { id: 'no', name: t('no', { ns: 'common' }) },
    ],
    [t],
  );

  const setGeodataConfiguration = context?.setGeodataConfiguration;
  const setSettingsValid = context?.setSettingsValid;

  const handleGeodataConfigurationChanged = useCallback(
    (data: { formData: GeodataConfigurationData; valid: boolean }) => {
      setGeodataConfiguration?.(data.formData);
      setSettingsValid?.(data.valid);
    },
    [setGeodataConfiguration, setSettingsValid],
  );

  if (!isDefined(context)) {
    return null;
  }

  return (
    <Container>
      <h4>{t('settings.title', { ns: 'cloudProcessing' })}</h4>
      <GeodataConfiguration
        defaultValues={context.geodataConfiguration}
        processType={context.selectedPackage}
        onChanged={handleGeodataConfigurationChanged}
      />

      {context.gcpEnabled && (
        <>
          {!context.disableInteractiveSetup && (
            <LabelledContainer
              infoTooltip={useInteractiveSetupTooltip}
              text={t('geodataSetup.useInteractiveSetup', { ns: 'cloudProcessing' })}
            >
              {(formElementId) => (
                <Select
                  className="form-select"
                  disabled={
                    context.addons?.gcpAdjustment?.enabled === true || context.imageFiles.length > 0
                  }
                  id={formElementId()}
                  options={yesNoOptions}
                  value={context.useInteractiveSetup ? 'yes' : 'no'}
                  width={250}
                  onChange={(e) => context.setUseInteractiveSetup(e.target.value === 'yes')}
                />
              )}
            </LabelledContainer>
          )}
          <LabelledContainer
            infoTooltip={skipGcpStepTooltip}
            text={t('geodataSetup.skipGcpStep', { ns: 'cloudProcessing' })}
          >
            {(formElementId) => (
              <Select
                className="form-select"
                disabled={
                  context.useInteractiveSetup || context.addons?.gcpAdjustment?.enabled === true
                }
                id={formElementId()}
                options={yesNoOptions}
                value={context.skipGcpStep ? 'yes' : 'no'}
                width={250}
                onChange={(e) => context.setSkipGcpStep(e.target.value === 'yes')}
              />
            )}
          </LabelledContainer>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;
